import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  EditIcon, DeleteIcon, ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

/* ─── Sabitler ─────────────────────────────────────────────── */
const SOURCE_TYPES = [
  { value: 1, label: 'API' },
  { value: 2, label: 'Veritabanı' },
  { value: 3, label: 'Excel' },
  { value: 4, label: 'JSON' },
];

const STATUS_MAP = {
  0: { label: 'Hiç Çalışmadı', tone: 'neutral' },
  1: { label: 'Çalışıyor',      tone: 'wait' },
  2: { label: 'Başarılı',       tone: 'ok' },
  3: { label: 'Başarısız',      tone: 'bad' },
  4: { label: 'Kısmen Başarılı', tone: 'wait' },
};

const STATUS_FILTER_OPTIONS = Object.entries(STATUS_MAP).map(([value, info]) => ({ value, label: info.label }));
const SOURCE_FILTER_OPTIONS = SOURCE_TYPES.map((t) => ({ value: String(t.value), label: t.label }));

const COLUMN_FILTER_META = {
  name:           { type: 'text' },
  sourceType:     { type: 'select', options: SOURCE_FILTER_OPTIONS },
  cronExpression: { type: 'text' },
  lastStatus:     { type: 'select', options: STATUS_FILTER_OPTIONS },
  isActive:       { type: 'select', options: [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }] },
};

const SEARCH_FIELDS = ['name', 'description'];

/* ─── Yardımcılar ───────────────────────────────────────────── */
function compareValues(left, right, dir) {
  const a = left ?? '';
  const b = right ?? '';
  return dir === 'asc'
    ? String(a).localeCompare(String(b), 'tr', { sensitivity: 'base' })
    : String(b).localeCompare(String(a), 'tr', { sensitivity: 'base' });
}

const fmtDate = (d) => d ? new Date(d).toLocaleString('tr-TR') : '—';

/* ─── Bileşen ───────────────────────────────────────────────── */
export function EtlPipelinePage() {
  usePageMeta('ETL Pipeline Tanımları', "Veri aktarım pipeline'larını tanımlayın, aktifleştirin ve çalıştırın.");
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);
  const [toast, setToast]             = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [sortField, setSortField]       = useState('name');
  const [sortDir, setSortDir]           = useState('asc');
  const [filterInput, setFilterInput]   = useState('');
  const [filterText, setFilterText]     = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [pageSize, setPageSize]         = useState(10);
  const [currentPage, setCurrentPage]   = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  /* Listeyi çek */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/Etl/pipelines', { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(Array.isArray(json.data) ? json.data : (Array.isArray(json) ? json : []));
    } catch (err) {
      showToast('error', `Pipeline listesi yüklenemedi: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Serbest metin araması (client-side, OData yok) */
  const searchedRows = useMemo(() => {
    const q = filterText.trim().toLocaleLowerCase('tr-TR');
    if (!q) return rows;
    return rows.filter(r => SEARCH_FIELDS.some(f => (r[f] || '').toLocaleLowerCase('tr-TR').includes(q)));
  }, [rows, filterText]);

  /* Kolon filtreleri (client-side) */
  const filteredRows = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, f]) => f != null);
    if (active.length === 0) return searchedRows;
    return searchedRows.filter((row) => active.every(([field, filter]) => {
      const meta = COLUMN_FILTER_META[field];
      if (!meta || filter == null) return true;
      if (meta.type === 'text') {
        const needle = String(filter.value ?? '').toLocaleLowerCase('tr-TR').trim();
        if (!needle) return true;
        return String(row[field] ?? '').toLocaleLowerCase('tr-TR').includes(needle);
      }
      if (meta.type === 'select') {
        const want = filter.value;
        if (want === '' || want == null) return true;
        return String(row[field] ?? '') === String(want);
      }
      return true;
    }));
  }, [searchedRows, columnFilters]);

  const sortedRows = useMemo(() =>
    [...filteredRows].sort((a, b) => compareValues(a[sortField], b[sortField], sortDir)),
    [filteredRows, sortField, sortDir]
  );

  const totalCount  = sortedRows.length;
  const visibleRows = useMemo(
    () => sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedRows, currentPage, pageSize]
  );

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (columnId, next) => { setColumnFilters((prev) => ({ ...prev, [columnId]: next })); setCurrentPage(1); };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  };

  /* Detaya git */
  const openNew = () => navigate('/etl-pipelines/new');
  const openDetail = (row) => navigate(`/etl-pipelines/${row.id}`, { state: { row } });
  const openEditRow = (row) => navigate(`/etl-pipelines/${row.id}?edit=1`, { state: { row } });

  const handleDelete = async () => {
    if (!deleteConfirm?.id) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/Etl/pipelines/${deleteConfirm.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Silme başarısız');
      if (selected?.id === deleteConfirm.id) setSelected(null);
      setDeleteConfirm(null);
      showToast('success', 'ETL Pipeline silindi');
      fetchData();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* ── TanStack kolonları ── */
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Pipeline Adı', meta: { filterType: 'text' },
      cell: (info) => <strong>{info.getValue() || '-'}</strong>,
    }),
    columnHelper.accessor('sourceType', {
      header: 'Kaynak Tipi', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        const st = SOURCE_TYPES.find(t => t.value === v);
        return <span className="c360-erp-chip">{st?.label ?? v}</span>;
      },
    }),
    columnHelper.accessor('cronExpression', {
      header: 'Zamanlama', meta: { filterType: 'text' },
      cell: (info) => <code style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{info.getValue() || '—'}</code>,
    }),
    columnHelper.accessor('lastStatus', {
      header: 'Son Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const status = STATUS_MAP[info.getValue()] ?? STATUS_MAP[0];
        return <StatusBadge tone={status.tone}>{status.label}</StatusBadge>;
      },
    }),
    columnHelper.accessor('lastRunAt', {
      header: 'Son Çalışma', enableColumnFilter: false,
      cell: (info) => <span style={{ color: 'var(--text-secondary)' }}>{fmtDate(info.getValue())}</span>,
    }),
    columnHelper.accessor('isActive', {
      header: 'Aktif', meta: { filterType: 'select' },
      cell: (info) => (
        <StatusBadge tone={info.getValue() ? 'ok' : 'neutral'}>{info.getValue() ? 'Aktif' : 'Pasif'}</StatusBadge>
      ),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          {canEdit
            ? <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>
            : <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="İzle" aria-label="İzle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setCurrentPage(1);
    if (!first) { setSortField('name'); setSortDir('asc'); return; }
    setSortField(toODataSortField(first.id));
    setSortDir(first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: visibleRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = () => {
    if (sortedRows.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
    const cols = [
      { field: 'name', headerName: 'Pipeline Adı' },
      { field: 'sourceType', headerName: 'Kaynak Tipi', valueGetter: ({ row }) => SOURCE_TYPES.find(t => t.value === row.sourceType)?.label ?? row.sourceType },
      { field: 'cronExpression', headerName: 'Zamanlama' },
      { field: 'lastStatus', headerName: 'Son Durum', valueGetter: ({ row }) => STATUS_MAP[row.lastStatus]?.label ?? '—' },
      { field: 'lastRunAt', headerName: 'Son Çalışma', valueGetter: ({ row }) => fmtDate(row.lastRunAt) },
      { field: 'isActive', headerName: 'Aktif', valueGetter: ({ row }) => (row.isActive ? 'Evet' : 'Hayır') },
    ];
    const dateStr = new Date().toISOString().slice(0, 10);
    exportToExcel(sortedRows, cols, `etl-pipeline_${dateStr}`);
    showToast('success', `${sortedRows.length} kayıt dışa aktarıldı`);
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Pipeline adı ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={visibleRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  /* ── Render ── */
  return (
    <div style={s.container}>
      <ListPageShell
        title="ETL Pipeline Tanımları"
        subtitle="Veri aktarım pipeline'larını tanımlayın, aktifleştirin ve çalıştırın."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Pipeline</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {/* Silme onayı */}
      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Pipeline Sil</h3>
              <button style={s.closeBtn} onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                <strong>{deleteConfirm.name}</strong> pipeline kaydını silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default EtlPipelinePage;
